import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Loader2, Check, Search, Save, Printer, Sparkles, Download } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuthHeaders = () => {
  const token = localStorage.getItem('tannou_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// ── Palettes proposees. Chaque couleur affiche son code hex dessous. ─────────
const PALETTES = [
  { id: 'moderne_tech', name: 'Moderne / Tech', colors: ['#0F172A', '#3B82F6', '#22D3EE', '#E2E8F0', '#F8FAFC'] },
  { id: 'corporate', name: 'Corporate / Confiance', colors: ['#1E3A8A', '#2563EB', '#60A5FA', '#DBEAFE', '#F8FAFC'] },
  { id: 'chaleureux', name: 'Chaleureux / Artisan', colors: ['#7C2D12', '#EA580C', '#F59E0B', '#FED7AA', '#FFFBEB'] },
  { id: 'nature', name: 'Nature / Bien-etre', colors: ['#14532D', '#16A34A', '#4ADE80', '#DCFCE7', '#F7FEE7'] },
  { id: 'luxe', name: 'Luxe / Elegant', colors: ['#1C1917', '#B45309', '#A8A29E', '#D6D3D1', '#FAFAF9'] },
  { id: 'feminin', name: 'Feminin / Doux', colors: ['#831843', '#EC4899', '#F472B6', '#FBCFE8', '#FDF2F8'] },
  { id: 'creatif', name: 'Creatif / Vif', colors: ['#4C1D95', '#8B5CF6', '#FBBF24', '#FCA5A5', '#FEF3C7'] },
  { id: 'minimal', name: 'Minimaliste / N&B', colors: ['#000000', '#404040', '#A3A3A3', '#E5E5E5', '#FFFFFF'] },
];

// ── Familles de typo. La police reelle est chargee via public/index.html. ────
const TYPOS = [
  { id: 'moderne', name: 'Moderne', font: "'Poppins', system-ui, sans-serif", hint: 'Epure, lisible, tech. Police : Poppins.' },
  { id: 'classique', name: 'Classique', font: "'Playfair Display', Georgia, serif", hint: 'Elegant, editorial, confiance. Police : Playfair Display.' },
  { id: 'chaleureux', name: 'Chaleureux', font: "'Quicksand', 'Trebuchet MS', sans-serif", hint: 'Accessible, doux, familial. Police : Quicksand.' },
  { id: 'caractere', name: 'De caractere', font: "'Bricolage Grotesque', 'Arial Black', sans-serif", hint: 'Affirme, creatif, memorable. Police : Bricolage Grotesque.' },
];

const MOODS = ['Moderne', 'Classique', 'Chaleureux', 'Luxe', 'Technique', 'Fun', 'Minimaliste', 'Nature'];

const FEATURES = [
  'Formulaire de contact', 'Prise de rendez-vous en ligne', 'Paiement en ligne (Stripe/PayPal)',
  'Boutique en ligne', 'Blog / actualites', 'Espace membre', 'Multilingue', 'Newsletter',
  'Avis clients', 'Galerie photos', 'Carte / plan d’acces', 'Espace admin (modifier le contenu soi-meme)',
];

const DOCS = [
  { key: 'siret', label: 'Numero SIRET ou avis de situation INSEE' },
  { key: 'facturation', label: 'Coordonnees de facturation (adresse, raison sociale)' },
  { key: 'email_pro', label: 'Email professionnel souhaite' },
  { key: 'telephone', label: 'Telephone' },
  { key: 'logo', label: 'Logo et elements de marque existants' },
  { key: 'textes', label: 'Textes / contenus a integrer' },
  { key: 'photos', label: 'Photos / visuels' },
  { key: 'rib', label: 'RIB (uniquement si paiement en ligne sur le site)' },
  { key: 'acces_domaine', label: 'Acces au domaine actuel (si le client en a deja un)' },
];

const EMPTY = {
  client_name: '', client_email: '', client_phone: '', company_name: '',
  site_type: '', main_goal: '', reference_sites: '', page_count: '',
  has_branding: '', branding_notes: '', logo_type: '', mood: [], palette_id: '', palette_custom: '', typo_id: '',
  content_texts_by: '', content_photos_by: '', content_notes: '',
  features: [], has_domain: '', current_domain: '', domain_ideas: '',
  docs: {}, budget_range: '', desired_launch: '', estimated_delivery: '',
  cadrage_fee: 250, total_estimate: 0, payment_notes: '', notes: '', status: 'brouillon',
  promo_code: '', promo_percent: 0, promo_applies_to: 'balance',
  promo_justificatif_ok: false, promo_insee_ok: false, promo_attestation_ok: false,
};

export const CahierDesCharges = () => {
  const navigate = useNavigate();
  const { briefId } = useParams();
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState(briefId || null);
  const [domainQuery, setDomainQuery] = useState('');
  const [domainResult, setDomainResult] = useState(null);
  const [domainChecking, setDomainChecking] = useState(false);
  const [promoInput, setPromoInput] = useState('');
  const [promoChecking, setPromoChecking] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [generatingSite, setGeneratingSite] = useState(false);

  const requireAuth = useCallback(async () => {
    const headers = getAuthHeaders();
    if (!headers.Authorization) { navigate('/login'); return false; }
    try {
      const res = await axios.post(`${API}/auth/verify`, {}, { headers });
      if (!res.data.valid) { navigate('/login'); return false; }
      return true;
    } catch { navigate('/login'); return false; }
  }, [navigate]);

  useEffect(() => {
    (async () => {
      const ok = await requireAuth();
      if (!ok) return;
      if (briefId) {
        try {
          const res = await axios.get(`${API}/briefs/${briefId}`, { headers: getAuthHeaders() });
          setForm({ ...EMPTY, ...res.data });
          if (res.data.ai_analysis) setAnalysis(res.data.ai_analysis);
        } catch { toast.error('Cahier des charges introuvable'); }
      }
      setCheckingAuth(false);
    })();
  }, [briefId, requireAuth]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleArr = (k, v) => setForm((f) => ({ ...f, [k]: f[k].includes(v) ? f[k].filter((x) => x !== v) : [...f[k], v] }));
  const toggleDoc = (k) => setForm((f) => ({ ...f, docs: { ...f.docs, [k]: !f.docs[k] } }));

  const checkDomain = async () => {
    if (!domainQuery.trim()) return;
    setDomainChecking(true);
    setDomainResult(null);
    try {
      const res = await axios.get(`${API}/tools/check-domain`, { params: { name: domainQuery }, headers: getAuthHeaders() });
      setDomainResult(res.data.results);
    } catch { toast.error('Verification impossible'); }
    setDomainChecking(false);
  };

  const applyPromo = async () => {
    const code = promoInput.trim().toUpperCase();
    if (!code) return;
    setPromoChecking(true);
    try {
      const res = await axios.get(`${API}/promo-codes/validate/${encodeURIComponent(code)}`, { headers: getAuthHeaders() });
      setForm((f) => ({
        ...f,
        promo_code: res.data.code,
        promo_percent: res.data.percent,
        promo_applies_to: res.data.applies_to,
      }));
      toast.success(`Code ${res.data.code} applique (-${res.data.percent}%). ${res.data.note || ''}`, { duration: 8000 });
    } catch {
      toast.error('Code inconnu ou desactive');
    }
    setPromoChecking(false);
  };

  const removePromo = () => {
    setForm((f) => ({ ...f, promo_code: '', promo_percent: 0, promo_justificatif_ok: false, promo_insee_ok: false, promo_attestation_ok: false }));
    setPromoInput('');
  };

  const printAttestation = () => {
    const w = window.open('', '_blank');
    if (!w) return;
    const nom = (form.client_name || '.....................................');
    const soc = form.company_name ? ` (${form.company_name})` : '';
    w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Attestation sur l'honneur - Code KAMAGUY</title>
      <style>body{font-family:Arial,sans-serif;max-width:720px;margin:40px auto;padding:0 40px;line-height:1.7;color:#1e293b}
      h1{font-size:18px;text-align:center;margin-bottom:32px}.line{border-bottom:1px solid #94a3b8;display:inline-block;min-width:180px}
      .sig{margin-top:60px;display:flex;justify-content:space-between}.box{border:1px solid #cbd5e1;height:110px;width:280px;border-radius:8px}
      p{margin:14px 0}.small{font-size:12px;color:#64748b;margin-top:40px}</style></head><body>
      <h1>Attestation sur l'honneur - Code promotionnel KAMAGUY</h1>
      <p>Je soussigné(e) <strong>${nom}</strong>${soc},</p>
      <p>déclare sur l'honneur :</p>
      <p>&bull; être <span class="line">&nbsp;</span> (particulier / auto-entrepreneur / micro-entreprise / association / entreprise créée le <span class="line">&nbsp;</span>) ;</p>
      <p>&bull; être domicilié(e) en <span class="line">&nbsp;</span> (Guadeloupe 971 / Martinique 972 / Guyane 973) ;</p>
      <p>&bull; ne pas être une société établie (SARL / SAS) de plus de 2 ans d'activité ;</p>
      <p>&bull; remplir l'ensemble des conditions du code promotionnel KAMAGUY.</p>
      <p>Je reconnais que toute fausse déclaration entraîne l'annulation immédiate de la réduction de 50 % et la facturation de la prestation au prix plein.</p>
      <p>Je joins à la présente : un justificatif de domicile de moins de 3 mois à mon nom, et mon avis de situation INSEE (ou extrait KBIS).</p>
      <div class="sig">
        <div>Fait à <span class="line">&nbsp;</span><br><br>Le <span class="line">&nbsp;</span></div>
        <div>Signature<br><div class="box"></div></div>
      </div>
      <p class="small">AGENTORY se réserve le droit de refuser l'application du code au cas par cas.</p>
      </body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 300);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, cadrage_fee: Number(form.cadrage_fee) || 0, total_estimate: Number(form.total_estimate) || 0 };
      if (savedId) {
        await axios.put(`${API}/briefs/${savedId}`, { ...payload, id: savedId }, { headers: getAuthHeaders() });
        toast.success('Enregistre');
      } else {
        const res = await axios.post(`${API}/briefs`, payload, { headers: getAuthHeaders() });
        setSavedId(res.data.id);
        window.history.replaceState(null, '', `/cahier-des-charges/${res.data.id}`);
        toast.success('Cahier des charges cree');
      }
    } catch { toast.error('Echec de l’enregistrement'); }
    setSaving(false);
  };

  const runAnalysis = async () => {
    if (!savedId) { toast.error('Enregistre d\'abord le cahier des charges'); return; }
    setAnalyzing(true);
    try {
      const res = await axios.post(`${API}/agent/analyze-project`, { brief_id: savedId }, { headers: getAuthHeaders() });
      const a = res.data;
      setAnalysis(a);
      // Pre-remplit ce qui peut l'etre, sans ecraser ce que Kathy a deja saisi a la main.
      setForm((f) => ({
        ...f,
        site_type: f.site_type || (a.sitemap ? 'Vitrine' : f.site_type),
        page_count: f.page_count || (a.sitemap ? String(a.sitemap.length) : f.page_count),
        features: f.features.length ? f.features : (a.recommended_features || []),
        palette_id: f.palette_id || a.suggested_palette_id || '',
        typo_id: f.typo_id || a.suggested_typo_id || '',
        total_estimate: Number(f.total_estimate) > 0 ? f.total_estimate : (a.price_estimate_eur || 0),
        estimated_delivery: f.estimated_delivery || a.timeline || '',
      }));
      toast.success('Analyse terminee, champs pre-remplis. Relis et ajuste.');
    } catch (e) {
      toast.error('Analyse impossible : ' + (e?.response?.data?.detail || 'erreur'));
    }
    setAnalyzing(false);
  };

  const generateSite = async () => {
    if (!analysis) { toast.error('Lance d\'abord l\'analyse'); return; }
    setGeneratingSite(true);
    try {
      const res = await axios.post(`${API}/agent/generate-scaffold`, { brief: form, analysis }, { headers: getAuthHeaders() });
      const blob = new Blob([res.data.html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const slug = (form.company_name || form.client_name || 'site').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      a.download = `${slug || 'site'}-depart.html`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Site de depart telecharge');
    } catch (e) {
      toast.error('Generation impossible : ' + (e?.response?.data?.detail || 'erreur'));
    }
    setGeneratingSite(false);
  };

  if (checkingAuth) {
    return <div className="min-h-screen pt-24 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  const total = Number(form.total_estimate) || 0;
  const cadrage = Number(form.cadrage_fee) || 0;
  const soldeBrut = Math.max(0, total - cadrage);
  const promoPct = Number(form.promo_percent) || 0;
  // La reduction porte sur le solde (hors forfait cadrage) - choix de Kathy.
  const solde = form.promo_code && promoPct > 0
    ? soldeBrut * (1 - promoPct / 100)
    : soldeBrut;
  const moitie = solde / 2;
  const economie = soldeBrut - solde;

  return (
    <div className="min-h-screen pt-24 pb-16 bg-slate-50 print:pt-4 print:bg-white" data-testid="cahier-des-charges-page">
      <div className="max-w-3xl mx-auto px-6">
        <div className="flex items-center justify-between mb-6 print:hidden">
          <h1 className="font-heading font-bold text-2xl text-slate-900">Cahier des charges</h1>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => window.print()} className="gap-2"><Printer className="w-4 h-4" /> Imprimer</Button>
            <Button onClick={save} disabled={saving} className="bg-primary hover:bg-primary-hover text-white gap-2">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Enregistrer
            </Button>
          </div>
        </div>

        <div className="space-y-6">
          {/* Agent IA */}
          <Card className="border-primary/30 print:hidden">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2"><Sparkles className="w-5 h-5 text-primary" /> Agent IA</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-slate-500">
                Analyse la demande, pre-remplit le cahier des charges (arborescence, fonctionnalites, palette,
                typo, estimation prix/delai), redige le contenu des pages, et genere un site de depart code.
              </p>
              <div className="flex flex-wrap gap-2">
                <Button type="button" onClick={runAnalysis} disabled={analyzing || !savedId} className="bg-primary hover:bg-primary-hover text-white gap-2">
                  {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  Analyser la demande
                </Button>
                {analysis && (
                  <Button type="button" variant="outline" onClick={generateSite} disabled={generatingSite} className="gap-2">
                    {generatingSite ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                    Telecharger le site de depart (.html)
                  </Button>
                )}
              </div>
              {!savedId && <p className="text-xs text-amber-600">Enregistre le cahier des charges avant de lancer l'analyse.</p>}

              {analysis && (
                <div className="mt-2 space-y-4 text-sm border-t pt-4">
                  <div>
                    <p className="font-semibold">Synthese</p>
                    <p className="text-slate-600">{analysis.summary}</p>
                  </div>
                  {analysis.sitemap && (
                    <div>
                      <p className="font-semibold">Arborescence proposee</p>
                      <ul className="list-disc pl-5 text-slate-600">
                        {analysis.sitemap.map((p, i) => <li key={i}><strong>{p.page}</strong>{p.but ? ` : ${p.but}` : ''}</li>)}
                      </ul>
                    </div>
                  )}
                  {analysis.pages_content && (
                    <div>
                      <p className="font-semibold">Contenu redige des pages</p>
                      <div className="space-y-3 mt-1">
                        {analysis.pages_content.map((pg, i) => (
                          <details key={i} className="rounded border border-slate-200 p-2">
                            <summary className="cursor-pointer font-medium">{pg.page}</summary>
                            <div className="mt-2 space-y-2 text-slate-600">
                              {(pg.sections || []).map((s, j) => (
                                <div key={j}>
                                  <p className="font-medium text-slate-800">{s.heading}</p>
                                  <p className="whitespace-pre-wrap">{s.body}</p>
                                </div>
                              ))}
                              {pg.cta && <p className="text-primary">Bouton : {pg.cta}</p>}
                            </div>
                          </details>
                        ))}
                      </div>
                    </div>
                  )}
                  {analysis.tech_checklist && (
                    <div>
                      <p className="font-semibold">Checklist technique</p>
                      <ul className="list-disc pl-5 text-slate-600">
                        {analysis.tech_checklist.map((c, i) => <li key={i}>{c}</li>)}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Client */}
          <Card><CardHeader><CardTitle className="text-base">Client</CardTitle></CardHeader>
            <CardContent className="grid sm:grid-cols-2 gap-4">
              <Field label="Nom du contact"><Input value={form.client_name} onChange={(e) => set('client_name', e.target.value)} /></Field>
              <Field label="Entreprise"><Input value={form.company_name} onChange={(e) => set('company_name', e.target.value)} /></Field>
              <Field label="Email"><Input value={form.client_email} onChange={(e) => set('client_email', e.target.value)} /></Field>
              <Field label="Telephone"><Input value={form.client_phone} onChange={(e) => set('client_phone', e.target.value)} /></Field>
            </CardContent>
          </Card>

          {/* Projet */}
          <Card><CardHeader><CardTitle className="text-base">Le projet</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <Field label="Type de site">
                <ChoiceRow value={form.site_type} onChange={(v) => set('site_type', v)} options={['Vitrine', 'Boutique en ligne', 'Blog', 'Autre']} />
              </Field>
              <Field label="Objectif principal"><Textarea rows={2} value={form.main_goal} onChange={(e) => set('main_goal', e.target.value)} placeholder="Vendre, etre trouve sur Google, prendre des RDV, presenter une activite..." /></Field>
              <Field label="Sites de reference (liens + ce qui plait)"><Textarea rows={2} value={form.reference_sites} onChange={(e) => set('reference_sites', e.target.value)} /></Field>
              <Field label="Nombre de pages estime"><Input value={form.page_count} onChange={(e) => set('page_count', e.target.value)} placeholder="ex : 5-6 pages" /></Field>
            </CardContent>
          </Card>

          {/* Identite visuelle */}
          <Card><CardHeader><CardTitle className="text-base">Identite visuelle</CardTitle></CardHeader>
            <CardContent className="space-y-5">
              <Field label="Le client a-t-il deja une identite ? (logo, couleurs, charte)">
                <ChoiceRow value={form.has_branding} onChange={(v) => set('has_branding', v)} options={['Oui, complete', 'Partielle', 'Non, a creer']} />
              </Field>
              <Field label="Notes sur l'existant"><Textarea rows={2} value={form.branding_notes} onChange={(e) => set('branding_notes', e.target.value)} /></Field>

              <Field label="Logo souhaite">
                <ChoiceRow value={form.logo_type} onChange={(v) => set('logo_type', v)} options={['Lettres seules (nom stylise)', 'Avec un symbole / icone', 'A definir']} />
              </Field>

              <Field label="Ambiance (plusieurs choix possibles)">
                <div className="flex flex-wrap gap-2">
                  {MOODS.map((m) => (
                    <button key={m} type="button" onClick={() => toggleArr('mood', m)}
                      className={`px-3 py-1.5 rounded-full text-sm border ${form.mood.includes(m) ? 'border-primary bg-primary text-white' : 'border-slate-200'}`}>{m}</button>
                  ))}
                </div>
              </Field>

              <Field label="Palette de couleurs">
                <div className="grid sm:grid-cols-2 gap-3">
                  {PALETTES.map((p) => (
                    <button key={p.id} type="button" onClick={() => set('palette_id', p.id)}
                      className={`text-left p-3 rounded-xl border-2 ${form.palette_id === p.id ? 'border-primary' : 'border-slate-200'}`}>
                      <div className="flex gap-1 mb-2">
                        {p.colors.map((c) => (
                          <div key={c} className="flex-1">
                            <div className="h-8 rounded" style={{ background: c, border: '1px solid rgba(0,0,0,.1)' }} />
                            <div className="text-[10px] text-slate-500 text-center mt-1">{c}</div>
                          </div>
                        ))}
                      </div>
                      <div className="text-sm font-medium">{p.name}</div>
                    </button>
                  ))}
                </div>
                <Input className="mt-3" value={form.palette_custom} onChange={(e) => set('palette_custom', e.target.value)} placeholder="Ou couleurs imposees par le client (codes hex)" />
              </Field>

              <Field label="Style d'ecriture (typographie)">
                <div className="grid sm:grid-cols-2 gap-3">
                  {TYPOS.map((tp) => (
                    <button key={tp.id} type="button" onClick={() => set('typo_id', tp.id)}
                      className={`text-left p-3 rounded-xl border-2 ${form.typo_id === tp.id ? 'border-primary' : 'border-slate-200'}`}>
                      <div style={{ fontFamily: tp.font }} className="text-xl text-slate-900">Votre site web</div>
                      <div style={{ fontFamily: tp.font }} className="text-sm text-slate-600">Aa Bb Cc 0123 - {tp.name}</div>
                      <div className="text-[11px] text-slate-400 mt-1">{tp.hint}</div>
                    </button>
                  ))}
                </div>
              </Field>
            </CardContent>
          </Card>

          {/* Contenu */}
          <Card><CardHeader><CardTitle className="text-base">Contenu</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <Field label="Qui redige les textes ?">
                <ChoiceRow value={form.content_texts_by} onChange={(v) => set('content_texts_by', v)} options={['Le client', 'Agentory (facture en plus)']} />
              </Field>
              <Field label="Photos / visuels">
                <ChoiceRow value={form.content_photos_by} onChange={(v) => set('content_photos_by', v)} options={['Fournis par le client', 'Banque d’images', 'Shooting a prevoir']} />
              </Field>
              <Field label="Notes contenu"><Textarea rows={2} value={form.content_notes} onChange={(e) => set('content_notes', e.target.value)} /></Field>
            </CardContent>
          </Card>

          {/* Fonctionnalites */}
          <Card><CardHeader><CardTitle className="text-base">Fonctionnalites</CardTitle></CardHeader>
            <CardContent className="grid sm:grid-cols-2 gap-2">
              {FEATURES.map((f) => (
                <label key={f} className="flex items-center gap-2 text-sm p-2 rounded hover:bg-slate-50 cursor-pointer">
                  <Checkbox checked={form.features.includes(f)} onCheckedChange={() => toggleArr('features', f)} />
                  {f}
                </label>
              ))}
            </CardContent>
          </Card>

          {/* Nom de domaine */}
          <Card><CardHeader><CardTitle className="text-base">Nom de domaine</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <Field label="Le client a-t-il deja un domaine ?">
                <ChoiceRow value={form.has_domain} onChange={(v) => set('has_domain', v)} options={['Oui', 'Non']} />
              </Field>
              {form.has_domain === 'Oui' && (
                <Field label="Domaine actuel"><Input value={form.current_domain} onChange={(e) => set('current_domain', e.target.value)} placeholder="exemple.fr" /></Field>
              )}
              <Field label="Verifier une idee de nom">
                <div className="flex gap-2">
                  <Input value={domainQuery} onChange={(e) => setDomainQuery(e.target.value)} placeholder="monentreprise" />
                  <Button type="button" variant="outline" onClick={checkDomain} disabled={domainChecking} className="gap-2">
                    {domainChecking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Verifier
                  </Button>
                </div>
                {domainResult && (
                  <div className="mt-2 space-y-1 text-sm">
                    {Object.values(domainResult).map((r) => (
                      <div key={r.domain} className="flex items-center gap-2">
                        <span className="font-mono">{r.domain}</span>
                        <span className={r.status === 'probablement_libre' ? 'text-green-600' : r.status === 'pris' ? 'text-red-500' : 'text-slate-400'}>
                          {r.status === 'probablement_libre' ? 'probablement libre' : r.status === 'pris' ? 'deja pris' : 'inconnu'}
                        </span>
                      </div>
                    ))}
                    <p className="text-xs text-slate-400">A confirmer chez un registraire avant achat.</p>
                  </div>
                )}
              </Field>
              <Field label="Idees de noms notees"><Textarea rows={2} value={form.domain_ideas} onChange={(e) => set('domain_ideas', e.target.value)} /></Field>
            </CardContent>
          </Card>

          {/* Documents a fournir */}
          <Card><CardHeader><CardTitle className="text-base">Documents a fournir par le client</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {DOCS.map((d) => (
                <label key={d.key} className="flex items-center gap-2 text-sm p-2 rounded hover:bg-slate-50 cursor-pointer">
                  <Checkbox checked={!!form.docs[d.key]} onCheckedChange={() => toggleDoc(d.key)} />
                  <span>{d.label}</span>
                  {form.docs[d.key] && <Check className="w-4 h-4 text-green-500 ml-auto" />}
                </label>
              ))}
              <p className="text-xs text-slate-400 pt-2">Coche au fur et a mesure ce que le client a fourni.</p>
            </CardContent>
          </Card>

          {/* Budget, delai, paiement */}
          <Card><CardHeader><CardTitle className="text-base">Budget, delai et paiement</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Fourchette de budget du client"><Input value={form.budget_range} onChange={(e) => set('budget_range', e.target.value)} placeholder="ex : 1000-2000 EUR" /></Field>
                <Field label="Date de mise en ligne souhaitee"><Input value={form.desired_launch} onChange={(e) => set('desired_launch', e.target.value)} /></Field>
                <Field label="Delai annonce (apres acompte)"><Input value={form.estimated_delivery} onChange={(e) => set('estimated_delivery', e.target.value)} placeholder="ex : 3 a 5 semaines" /></Field>
                <Field label="Estimation totale (EUR)"><Input type="number" value={form.total_estimate} onChange={(e) => set('total_estimate', e.target.value)} /></Field>
                <Field label="Forfait cadrage (EUR, deductible du total)"><Input type="number" value={form.cadrage_fee} onChange={(e) => set('cadrage_fee', e.target.value)} /></Field>
              </div>

              {/* Code promo (KAMAGUY -50% sur le solde) */}
              <Field label="Code promo">
                {!form.promo_code ? (
                  <div className="flex gap-2">
                    <Input value={promoInput} onChange={(e) => setPromoInput(e.target.value)} placeholder="ex : KAMAGUY" />
                    <Button type="button" variant="outline" onClick={applyPromo} disabled={promoChecking}>
                      {promoChecking ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Appliquer'}
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="px-2 py-1 rounded bg-green-100 text-green-700 font-medium">{form.promo_code} : -{form.promo_percent}% sur le solde</span>
                      <button type="button" onClick={removePromo} className="text-slate-400 hover:text-red-500 text-xs underline">retirer</button>
                    </div>
                    <p className="text-xs text-slate-500">3 verifications obligatoires avant de confirmer la reduction :</p>
                    <label className="flex items-start gap-2 text-sm">
                      <Checkbox className="mt-0.5" checked={form.promo_justificatif_ok} onCheckedChange={() => set('promo_justificatif_ok', !form.promo_justificatif_ok)} />
                      <span>Justificatif de domicile &lt; 3 mois, au nom du client, code postal 971 / 972 / 973</span>
                    </label>
                    <label className="flex items-start gap-2 text-sm">
                      <Checkbox className="mt-0.5" checked={form.promo_insee_ok} onCheckedChange={() => set('promo_insee_ok', !form.promo_insee_ok)} />
                      <span>Avis de situation INSEE (ou KBIS) verifie : particulier / auto-entrepreneur / micro / association, OU entreprise de moins de 2 ans</span>
                    </label>
                    <label className="flex items-start gap-2 text-sm">
                      <Checkbox className="mt-0.5" checked={form.promo_attestation_ok} onCheckedChange={() => set('promo_attestation_ok', !form.promo_attestation_ok)} />
                      <span>Attestation sur l'honneur signee par le client</span>
                    </label>
                    <Button type="button" variant="outline" size="sm" onClick={printAttestation} className="gap-2">
                      <Printer className="w-4 h-4" /> Generer l'attestation a signer
                    </Button>
                    {!(form.promo_justificatif_ok && form.promo_insee_ok && form.promo_attestation_ok) && (
                      <p className="text-xs text-red-500">La reduction ne doit etre confirmee qu'une fois les 3 points verifies.</p>
                    )}
                  </div>
                )}
              </Field>

              <div className="p-4 rounded-xl bg-slate-100 text-sm space-y-1">
                <p className="font-semibold">Modele de paiement : forfait cadrage + 50/50</p>
                <p>1. <strong>{cadrage.toFixed(0)} EUR</strong> a l'issue de la visio : cahier des charges finalise. Non remboursable, deduit du total.</p>
                <p>2. <strong>{moitie.toFixed(0)} EUR</strong> (50% du reste) pour lancer le developpement.</p>
                <p>3. <strong>{moitie.toFixed(0)} EUR</strong> (50% du reste) avant la mise en ligne. Le site reste sur une adresse de test jusqu'au reglement.</p>
                {form.promo_code && promoPct > 0 && (
                  <p className="pt-1 text-green-700">Code {form.promo_code} : le client economise <strong>{economie.toFixed(0)} EUR</strong> sur le solde. Total a payer : {(cadrage + solde).toFixed(0)} EUR au lieu de {total.toFixed(0)} EUR.</p>
                )}
              </div>

              <Field label="Notes paiement"><Textarea rows={2} value={form.payment_notes} onChange={(e) => set('payment_notes', e.target.value)} /></Field>
            </CardContent>
          </Card>

          <Card><CardHeader><CardTitle className="text-base">Notes libres</CardTitle></CardHeader>
            <CardContent><Textarea rows={4} value={form.notes} onChange={(e) => set('notes', e.target.value)} /></CardContent>
          </Card>

          <div className="flex justify-end print:hidden">
            <Button onClick={save} disabled={saving} className="bg-primary hover:bg-primary-hover text-white gap-2">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Enregistrer le cahier des charges
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div className="space-y-2">
    <Label className="text-sm">{label}</Label>
    {children}
  </div>
);

const ChoiceRow = ({ value, onChange, options }) => (
  <div className="flex flex-wrap gap-2">
    {options.map((o) => (
      <button key={o} type="button" onClick={() => onChange(o)}
        className={`px-3 py-1.5 rounded-lg text-sm border ${value === o ? 'border-primary bg-primary text-white' : 'border-slate-200 hover:border-primary'}`}>
        {o}
      </button>
    ))}
  </div>
);
